import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Pencil, Trash, FloppyDisk, X, Buildings } from "@phosphor-icons/react";

export default function Companies() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: () => http.get("/companies").then(r => r.data) });
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: () => http.get("/brands").then(r => r.data) });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const canCreate = user?.role === "admin";
  const canEdit = canCreate;
  const canDelete = canCreate;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (modal.mode === "create") await http.post("/companies", form);
      else await http.patch(`/companies/${modal.id}`, form);
      qc.invalidateQueries({ queryKey: ["companies"] });
      setModal(null); setForm({ name: "" });
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Soft-delete company "${name}"?`)) return;
    try { await http.delete(`/companies/${id}`); qc.invalidateQueries({ queryKey: ["companies"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Tenancy</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Companies</h1>
        </div>
        {canCreate && <button onClick={() => { setForm({ name: "" }); setModal({ mode: "create" }); }} data-testid="company-new-button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
          <Plus size={14} weight="bold" /> Add company
        </button>}
      </div>

      <div className="space-y-4">
        {companies.map(c => (
          <div key={c.id} data-testid={`company-${c.id}`} className="rmms-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2"><Buildings size={14} /><div className="text-xs uppercase tracking-wider text-slate-500">Company</div></div>
                <div className="text-lg font-medium">{c.name}</div>
              </div>
              <div className="flex gap-1">
                {canEdit && <button onClick={() => { setForm({ name: c.name }); setModal({ mode: "edit", id: c.id }); }} data-testid={`company-edit-${c.id}`} className="h-8 w-8 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>}
                {canDelete && <button onClick={() => remove(c.id, c.name)} data-testid={`company-delete-${c.id}`} className="h-8 w-8 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={12} /></button>}
              </div>
            </div>
            <div className="mt-4 pl-4 border-l border-slate-200 space-y-2">
              {brands.filter(b => b.company_id === c.id).map(b => (
                <div key={b.id}>
                  <div className="font-medium text-sm">{b.name}</div>
                  <div className="pl-3 mt-0.5 space-y-0.5">
                    {stores.filter(s => s.brand_id === b.id).map(s => (
                      <div key={s.id} className="text-sm text-slate-600">· {s.name} <span className="text-xs text-slate-400">({s.district})</span></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="company-modal">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div><div className="overline">{modal.mode === "create" ? "New" : "Edit"}</div><h2 className="text-xl font-heading font-semibold tracking-tight mt-1">Company</h2></div>
              <button type="button" onClick={() => setModal(null)} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div>
              <label className="overline mb-1.5 block">Name *</label>
              <input required value={form.name} onChange={e => setForm({ name: e.target.value })} data-testid="company-name-input" className="rmms-input" />
            </div>
            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={() => setModal(null)} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="company-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
                <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
